import React from 'react';
import { Activity, Users, MousePointerClick, Zap } from 'lucide-react';

export interface AnalyticsMetrics {
  pageviews30d: number;
  uniqueVisitors30d: number;
  conversionRatePct: number;
  lcpP75Ms: number;
  clsP75: number;
  inpP75Ms: number;
}

export interface WebVitalsDashboardProps {
  metrics: AnalyticsMetrics;
}

// WebVitalsDashboard displays cookieless traffic metrics and Core Web Vitals telemetry.
export function WebVitalsDashboard({ metrics }: WebVitalsDashboardProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Activity className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Traffic & Core Web Vitals Analytics</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-muted-foreground mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Pageviews (30d)</span>
            <Users className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics.pageviews30d.toLocaleString()}</p>
          <span className="text-xs text-muted-foreground mt-1 block">Cookieless visitor beacons</span>
        </div>

        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-muted-foreground mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Unique Visitors</span>
            <Users className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics.uniqueVisitors30d.toLocaleString()}</p>
          <span className="text-xs text-muted-foreground mt-1 block">Salted host-fingerprints</span>
        </div>

        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-muted-foreground mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Conversion Rate</span>
            <MousePointerClick className="h-4 w-4 text-accent" />
          </div>
          <p className="text-2xl font-bold text-accent">{metrics.conversionRatePct}%</p>
          <span className="text-xs text-muted-foreground mt-1 block">Form & checkout funnels</span>
        </div>
      </div>

      <div className="p-5 rounded-xl border border-border bg-background flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">p75 Core Web Vitals</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 rounded-lg border border-border bg-card flex justify-between items-center">
            <span className="text-xs text-muted-foreground">LCP (Largest Contentful Paint)</span>
            <span className="text-xs font-bold text-accent px-2 py-0.5 rounded bg-accent/15">{metrics.lcpP75Ms} ms</span>
          </div>
          <div className="p-3 rounded-lg border border-border bg-card flex justify-between items-center">
            <span className="text-xs text-muted-foreground">CLS (Cumulative Layout Shift)</span>
            <span className="text-xs font-bold text-accent px-2 py-0.5 rounded bg-accent/15">{metrics.clsP75}</span>
          </div>
          <div className="p-3 rounded-lg border border-border bg-card flex justify-between items-center">
            <span className="text-xs text-muted-foreground">INP (Interaction to Next Paint)</span>
            <span className="text-xs font-bold text-accent px-2 py-0.5 rounded bg-accent/15">{metrics.inpP75Ms} ms</span>
          </div>
        </div>
      </div>
    </div>
  );
}
